import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../ApplicationState';
import { useHistory, useParams } from 'react-router-dom';
import { SpinnerOverlay } from '../SpinnerOverlay';
import StudentHeroSegment from '../components/StudentHeroSegment';
import ToDoCard from '../components/ToDoCard';
import Button from '../components/Button';
import { listStudentAssignments } from '../BackendInterface';
import { secondarySchoolLessonNumberLookup } from '../GlobalFunctions';
import { Assignment, UrlParams } from '@peekapak/types/LessonPlanTypes';
import { InlineIcon } from '@iconify/react';
import eyeOutline from '@iconify-icons/ion/eye-outline';
import toDoHeader from '../images/ToDo_Page.png';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import styles from './ToDoConfirm.module.scss';

interface Props {
  currentToDo: Assignment;
  setCurrentToDo: React.Dispatch<React.SetStateAction<any>>;
  setGroupedToDoInfo: React.Dispatch<React.SetStateAction<any>>;
}

const ToDoConfirm = ({ setCurrentToDo, setGroupedToDoInfo, currentToDo }: Props) => {
  const [toDoArray, setToDoArray] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const { activity, language, grade, toDo }: UrlParams = useParams();
  const isHighSchool = parseInt(grade) > 8;

  const userId = useSelector((state: RootState) => {
    return state.user.userProfile!.userId;
  });

  const history = useHistory();

  const handleCardClick = (cardObject: Assignment) => {
    setCurrentToDo(cardObject);
    const groupToDosByModule = toDoArray.filter(
      (item: Assignment) =>
        item.module === cardObject.module && item.unit === cardObject.unit && item.lesson === cardObject.lesson,
    );
    const quizFirst = groupToDosByModule.sort((a, b) => a.todo.localeCompare(b.todo));
    quizFirst.length > 1 && setGroupedToDoInfo(quizFirst);
    history.push(`/studentportal/activity/${cardObject.module}/en/${grade}/toDo/${cardObject.createdAt}`);
  };

  const getToDos = useCallback(async () => {
    try {
      setIsLoading(true);
      const studentToDos = await listStudentAssignments(userId, language, grade);
      const excludeDone = studentToDos.filter((toDo) => toDo.status !== 'submitted');
      setToDoArray(excludeDone);
    } catch {
      console.log('Error fetching student ToDos');
    } finally {
      setIsLoaded(true);
      setIsLoading(false);
    }
  }, [toDoArray]);

  useEffect(() => {
    !isLoaded && getToDos();
  }, []);

  return (
    <>
      <SpinnerOverlay isShow={isLoading} />
      <StudentHeroSegment confirm backgroundImage={isHighSchool ? toDoHeaderHS : toDoHeader}>
        <div className={styles.heroText}>
          <h1>You Did It!</h1>

          {currentToDo && (
            <div className={styles.assignmentInfo}>
              <h4>
                {currentToDo.unitDisplayName}&nbsp;&nbsp;&#8226;&nbsp; Lesson&nbsp;
                {secondarySchoolLessonNumberLookup(currentToDo.lesson, isHighSchool)}:{' '}
                {currentToDo.lessonTitle ?? currentToDo.lesson}
              </h4>
              <h3>{currentToDo.todoTitle}</h3>
              <p>has been submitted to your teacher for review.</p>
            </div>
          )}

          <div className={styles.buttonRow}>
            <Button
              thinText
              small
              whiteBorder
              onClick={() => history.push(`/studentportal/activity/${activity}/${language}/${grade}/toDo/${toDo}`)}
            >
              <InlineIcon
                icon={eyeOutline}
                width={18}
                style={{
                  marginRight: '0.5em',
                  transform: 'translateY(2px)',
                }}
              />
              View Submission
            </Button>
            <Button darkBlue small thinText onClick={() => history.push('/studentportal/todos')}>
              View All To Dos
            </Button>
          </div>
        </div>
      </StudentHeroSegment>
      <div className={styles.cardContainer}>
        <h3>Open Assignments</h3>
        {toDoArray?.length > 0 ? (
          toDoArray?.map((x: Assignment) => (
            <ToDoCard key={x.createdAt} toDoObject={x} handleClick={handleCardClick} grade={parseInt(grade)} />
          ))
        ) : (
          <div className={styles.allDone}>
            <h3>You&apos;ve completed all your assignments...nice!</h3>
          </div>
        )}
      </div>
    </>
  );
};

export default ToDoConfirm;
